import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopyRounded'
import DeleteIcon from '@mui/icons-material/DeleteRounded'
import BlockIcon from '@mui/icons-material/BlockRounded'
import CheckCircleIcon from '@mui/icons-material/CheckCircleRounded'
import ShieldIcon from '@mui/icons-material/ShieldRounded'
import PersonIcon from '@mui/icons-material/PersonRounded'
import LockResetIcon from '@mui/icons-material/LockResetRounded'
import api, { apiErrorMessage } from '../api/client'
import { useAuth } from '../auth/useAuth'
import type { User } from '../api/types'

const formatLastConnection = (value: string | null) =>
  value ? new Date(value).toLocaleString() : 'Jamais'

export default function AdminPage() {
  const { user: currentUser } = useAuth()
  const [users, setUsers] = useState<User[]>([])
  const [error, setError] = useState('')
  const [inviteToken, setInviteToken] = useState<{ token: string; expiresAt: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [resetTarget, setResetTarget] = useState<User | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [resetError, setResetError] = useState('')

  useEffect(() => {
    let cancelled = false
    api
      .get<User[]>('/admin/users')
      .then((res) => {
        if (!cancelled) setUsers(res.data)
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err))
      })
    return () => {
      cancelled = true
    }
  }, [])

  const generateInviteToken = async () => {
    setError('')
    setBusy(true)
    try {
      const res = await api.post<{ token: string; expiresAt: string }>('/admin/invite-tokens')
      setInviteToken(res.data)
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const toggleActive = async (target: User) => {
    setError('')
    try {
      const res = await api.patch<User>(`/admin/users/${target.id}`, { active: !target.active })
      setUsers((prev) => prev.map((u) => (u.id === target.id ? res.data : u)))
    } catch (err) {
      setError(apiErrorMessage(err))
    }
  }

  const toggleRole = async (target: User) => {
    setError('')
    const role = target.role === 'admin' ? 'user' : 'admin'
    try {
      const res = await api.patch<User>(`/admin/users/${target.id}`, { role })
      setUsers((prev) => prev.map((u) => (u.id === target.id ? res.data : u)))
    } catch (err) {
      setError(apiErrorMessage(err))
    }
  }

  const openResetPassword = (target: User) => {
    setResetError('')
    setNewPassword('')
    setResetTarget(target)
  }

  const submitResetPassword = async () => {
    if (!resetTarget) return
    if (newPassword.length < 8) {
      setResetError('8 caractères minimum')
      return
    }
    setResetError('')
    setBusy(true)
    try {
      await api.post(`/admin/users/${resetTarget.id}/reset-password`, { password: newPassword })
      setResetTarget(null)
    } catch (err) {
      setResetError(apiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const deleteUser = async (target: User) => {
    if (!window.confirm(`Supprimer définitivement le compte ${target.email} ?`)) return
    setError('')
    try {
      await api.delete(`/admin/users/${target.id}`)
      setUsers((prev) => prev.filter((u) => u.id !== target.id))
    } catch (err) {
      setError(apiErrorMessage(err))
    }
  }

  const renderActions = (u: User) => {
    const isSelf = u.id === currentUser?.id
    return (
      <>
        <Tooltip title={u.role === 'admin' ? 'Rétrograder' : 'Promouvoir admin'}>
          <span>
            <IconButton size="small" onClick={() => toggleRole(u)} disabled={isSelf}>
              {u.role === 'admin' ? (
                <PersonIcon fontSize="small" />
              ) : (
                <ShieldIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={u.active ? 'Désactiver' : 'Activer'}>
          <span>
            <IconButton size="small" onClick={() => toggleActive(u)} disabled={isSelf}>
              {u.active ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Réinitialiser le mot de passe">
          <span>
            <IconButton size="small" onClick={() => openResetPassword(u)}>
              <LockResetIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Supprimer">
          <span>
            <IconButton size="small" onClick={() => deleteUser(u)} disabled={isSelf}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </>
    )
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h4">Administration</Typography>

      {error && <Alert severity="error">{error}</Alert>}

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Jeton d'invitation
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Requis pour créer un compte. Valide 1 heure, usage unique.
          </Typography>
          <Button variant="contained" onClick={generateInviteToken} disabled={busy}>
            Générer un jeton
          </Button>
          {inviteToken && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(245,245,245,0.05)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                wordBreak: 'break-all',
              }}
            >
              <Typography variant="body2" sx={{ flexGrow: 1, fontFamily: 'monospace' }}>
                {inviteToken.token}
              </Typography>
              <Tooltip title="Copier">
                <IconButton
                  size="small"
                  onClick={() => navigator.clipboard.writeText(inviteToken.token)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {inviteToken && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
              Expire le {new Date(inviteToken.expiresAt).toLocaleString()}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Utilisateurs
          </Typography>
          <Table size="small" sx={{ display: { xs: 'none', sm: 'table' } }}>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Rôle</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Créé le</TableCell>
                <TableCell>Dernière connexion</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => {
                return (
                  <TableRow key={u.id}>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={u.role === 'admin' ? 'Admin' : 'Utilisateur'}
                        color={u.role === 'admin' ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={u.active ? 'Actif' : 'Désactivé'}
                        color={u.active ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{new Date(u.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>{formatLastConnection(u.lastConnection)}</TableCell>
                    <TableCell align="right">{renderActions(u)}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          <Stack spacing={1.5} sx={{ display: { xs: 'flex', sm: 'none' } }}>
            {users.map((u) => (
              <Box key={u.id} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(245,245,245,0.05)' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                  {u.email}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                  <Chip
                    size="small"
                    label={u.role === 'admin' ? 'Admin' : 'Utilisateur'}
                    color={u.role === 'admin' ? 'primary' : 'default'}
                  />
                  <Chip
                    size="small"
                    label={u.active ? 'Actif' : 'Désactivé'}
                    color={u.active ? 'success' : 'error'}
                    variant="outlined"
                  />
                </Stack>
                <Box
                  sx={{
                    mt: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Créé le {new Date(u.createdAt).toLocaleDateString()}
                    <br />
                    Dernière connexion : {formatLastConnection(u.lastConnection)}
                  </Typography>
                  <Box>{renderActions(u)}</Box>
                </Box>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Dialog
        open={resetTarget !== null}
        onClose={() => setResetTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Nouveau mot de passe pour {resetTarget?.email}. L'utilisateur sera déconnecté de ses
            sessions actives.
          </Typography>
          {resetError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {resetError}
            </Alert>
          )}
          <TextField
            // Dialog opens on explicit user action; focusing the only field is expected
            // oxlint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            fullWidth
            type="password"
            label="Nouveau mot de passe"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submitResetPassword()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetTarget(null)}>Annuler</Button>
          <Button variant="contained" onClick={submitResetPassword} disabled={busy}>
            Réinitialiser
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}
